import { Injectable, inject } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Navigate, RouterDataResolved } from "@ngxs/router-plugin";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { NgxPermissionsService } from "ngx-permissions";
import { EMPTY } from "rxjs";
import { catchError, finalize, tap } from "rxjs/operators";
import { IRefreshToken } from "src/app/model/custom/IAuth";
import { OauthService } from "src/app/oauth/services/oauth.service";
import { IUser } from "../../model/custom/IUser";
import { IAuthenticationResult } from "../../model/custom/IUserLoginEvent";
import { AuthV1Service } from "../service/auth-v1.service";
import { AuthenticatedUserService } from "../service/authenticated-user.service";
import {
	StoredAuthRemoveAction,
	StoredAuthSetAction,
	StoredAuthUserRefreshTokenAction,
} from "./stored-auth.actions";

export class StoredAuthStateModel {
	public userToken: IAuthenticationResult;
	public user: IUser;
	public isPending: boolean;
	public isOauth: boolean;
}

@State<StoredAuthStateModel>({
	name: "storedAuth",
	defaults: {
		userToken: null,
		user: null,
		isPending: false,
		isOauth: false,
	},
})
@Injectable()
export class StoredAuthState {
	private authV1Service = inject(AuthV1Service);
	private jwtHelperService = inject(JwtHelperService);
	private authenticatedUserService = inject(AuthenticatedUserService);
	private oauthService = inject(OauthService);
	private ngxPermissionsService = inject(NgxPermissionsService);

	/** Inserted by Angular inject() migration for backwards compatibility */
	constructor(...args: unknown[]);

	constructor() {}
	@Selector()
	static authIdToken(state: StoredAuthStateModel): string {
		return state.userToken?.IdToken;
	}
	@Selector()
	static authRefreshToken(state: StoredAuthStateModel): string {
		return state.userToken?.RefreshToken;
	}
	@Selector()
	static isPending(state: StoredAuthStateModel): boolean {
		return state.userToken && state.isPending;
	}

	@Selector()
	static isOauth(state: StoredAuthStateModel): boolean {
		return state.isOauth;
	}
	@Selector()
	static userDomain(state: StoredAuthStateModel): string {
		if (!state.user?.email) {
			return "";
		}
		return state.user.email.split("@")[1];
	}

	@Selector()
	static isUserAuthenticated(state: StoredAuthStateModel): boolean {
		const jwtHelperService = new JwtHelperService();
		return (
			!!state.userToken &&
			!jwtHelperService.isTokenExpired(state.userToken?.IdToken)
		);
	}

	@Selector()
	static token(state: StoredAuthStateModel): IAuthenticationResult {
		return state.userToken;
	}
	@Selector()
	static user(state: StoredAuthStateModel): IUser {
		return state?.user;
	}
	@Selector()
	static groups(state: StoredAuthStateModel): string[] {
		if (!state.user) {
			return [];
		}
		return state.user["cognito:groups"];
	}

	@Selector()
	static roles(state: StoredAuthStateModel): string[] {
		if (!state.user) {
			return [];
		}
		return state.user["cognito:groups"] || [];
	}
	@Selector()
	static userLoginDetails(state: StoredAuthStateModel): string {
		return state.user.email || state.user.phone_number;
	}
	@Selector()
	static isActingAs(state: StoredAuthStateModel): string {
		return state.user["act-as:username"];
	}

	@Selector()
	static isSystemAdministratorRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return (
			state.user["cognito:groups"].join(",").indexOf("SystemAdministrator") !== -1
		);
	}
	@Selector()
	static isInInducteeRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return state.user["cognito:groups"].join(",").indexOf("Inductee") !== -1;
	}

	@Selector()
	static isUseNewUI(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return state.user["cognito:groups"].join(",").indexOf("UI_Use_V3") !== -1;
	}

	@Selector()
	static isCompanySupervisorRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return state.user["cognito:groups"].join(",").indexOf("CompanySupervisor") !== -1;
	}

	@Selector()
	static isCompanySiteManagerRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return (
			state.user["cognito:groups"].join(",").indexOf("CompanySiteManager") !== -1
		);
	}

	@Selector()
	static isCompanyAdministrationUserRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return (
			state.user["cognito:groups"]
				.join(",")
				.indexOf("CompanyAdministrationUser") !== -1
		);
	}

	@Selector()
	static IsCompanySiteAdministratorRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return (
			state.user["cognito:groups"]
				.join(",")
				.indexOf("CompanySiteAdministrationUser") !== -1
		);
	}

	@Selector()
	static isCompanyRootUserRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return state.user["cognito:groups"].indexOf("Company") !== -1;
	}

	@Selector()
	static isInAnyCompanyRole(state: StoredAuthStateModel): boolean {
		if (!state.user) {
			return false;
		}
		return state.user["cognito:groups"].join(",").indexOf("Company") !== -1;
	}

	@Selector([
		StoredAuthState.isCompanyAdministrationUserRole,
		StoredAuthState.isCompanyRootUserRole,
		StoredAuthState.isSystemAdministratorRole,
	])
	static IsCompanyOrSystemAdmin(
		isCompanyAdministrationUserRole,
		isCompanyRootUserRole,
		isSystemAdministratorRole,
	) {
		return (
			isCompanyAdministrationUserRole ||
			isCompanyRootUserRole ||
			isSystemAdministratorRole
		);
	}

	@Action(RouterDataResolved)
	RouterDataResolved(ctx: StateContext<StoredAuthStateModel>) {
		const { userToken } = ctx.getState();
		if (!userToken) {
			return EMPTY;
		}
		if (this.jwtHelperService.isTokenExpired(userToken.IdToken)) {
			return ctx.dispatch([new StoredAuthUserRefreshTokenAction()]);
		}
	}

	@Action(StoredAuthUserRefreshTokenAction)
	StoredAuthUserRefreshTokenAction(
		ctx: StateContext<StoredAuthStateModel>,
		action: StoredAuthUserRefreshTokenAction,
	) {
		const { userToken, user, isOauth } = ctx.getState();
		if (!userToken || !user) {
			return EMPTY;
		}

		const { RefreshToken } = userToken;

		const data: IRefreshToken = {
			refreshToken: RefreshToken,
			userid: user["cognito:username"],
		};

		ctx.patchState({
			isPending: true,
		});

		return (
			isOauth
				? this.oauthService.RefreshToken(data)
				: this.authenticatedUserService.RefreshToken(data)
		).pipe(
			tap((result: IAuthenticationResult) => {
				if (result?.RefreshToken) {
					return ctx.dispatch(new StoredAuthSetAction(result));
				} else {
					return ctx.dispatch([new StoredAuthRemoveAction()]);
				}
			}),
			catchError((err) => {
				console.log(err);
				return ctx.dispatch([
					new StoredAuthRemoveAction(),
					new Navigate(["auth"], {
						returnUrl: window.location.hash?.substring(1),
					}),
				]);
			}),
			finalize(() => {
				ctx.patchState({
					isPending: false,
				});
			}),
		);
	}

	@Action(StoredAuthSetAction)
	StoredAuthSetAction(
		ctx: StateContext<StoredAuthStateModel>,
		action: StoredAuthSetAction,
	) {
		if (!action.payload?.IdToken || action.payload.IdToken === "undefined") {
			ctx.dispatch([new StoredAuthRemoveAction()]);
			return;
		}
		const decodedToken = this.jwtHelperService.decodeToken(
			action.payload.IdToken,
		) as IUser;

		this.authV1Service.setAuthData(action.payload);

		ctx.patchState({
			user: decodedToken,
			userToken: action.payload,
			isOauth: decodedToken["cognito:groups"].join(",").indexOf("Oauth") !== -1,
		});
		this.ngxPermissionsService.loadPermissions(decodedToken["cognito:groups"]);
	}

	@Action(StoredAuthRemoveAction)
	StoredAuthRemoveAction(
		ctx: StateContext<StoredAuthStateModel>,
		action: StoredAuthRemoveAction,
	) {
		this.authV1Service.clearAuthData();
		ctx.patchState({
			user: undefined,
			userToken: undefined,
		});
	}
}
